import { useEffect, useMemo, useState } from "react";
import { SlidersHorizontal, X } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Slider } from "@/components/ui/slider";
import { Drawer, DrawerContent, DrawerDescription, DrawerFooter, DrawerHeader, DrawerTitle } from "@/components/ui/drawer";
import { ETHNICITY_FILTERS, ETHNICITY_LABEL, GENDER_LABEL, SERVICE_LABEL } from "@/lib/api";

const KEYS = ["area", "gender", "ethnicity", "service", "age_min", "age_max", "available", "stories", "price_min", "price_max"];
const ANY = "any";

export function readFilters(params) {
  const f = {};
  KEYS.forEach((k) => {
    const v = params.get(k);
    if (v) f[k] = v;
  });
  return f;
}

export function filtersToQuery(f) {
  const q = new URLSearchParams();
  Object.entries(f).forEach(([k, v]) => {
    if (v !== undefined && v !== null && v !== "" && v !== ANY) q.set(k, v);
  });
  return q;
}

function Controls({ value, onChange, areas, showArea }) {
  const set = (k, v) => onChange({ ...value, [k]: v === ANY ? undefined : v });
  const [range, setRange] = useState([Number(value.age_min || 18), Number(value.age_max || 60)]);
  useEffect(() => setRange([Number(value.age_min || 18), Number(value.age_max || 60)]), [value.age_min, value.age_max]);
  const [ageMin, ageMax] = range;
  const [priceRange, setPriceRange] = useState([Number(value.price_min || 0), Number(value.price_max || 1000)]);
  useEffect(() => setPriceRange([Number(value.price_min || 0), Number(value.price_max || 1000)]), [value.price_min, value.price_max]);
  const [priceMin, priceMax] = priceRange;
  const trig = "lux-input h-11 justify-between border-lux-border bg-lux-surface2 text-left text-sm [&>span]:truncate";
  return (
    <>
      {showArea && (
        <div className="min-w-[160px] flex-1">
          <label className="lux-label mb-1.5 block">Area</label>
          <Select value={value.area || ANY} onValueChange={(v) => set("area", v)}>
            <SelectTrigger className={trig} data-testid="results-filter-area" aria-label="Area"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value={ANY}>All areas</SelectItem>
              {areas.map((a) => <SelectItem key={a.slug} value={a.slug}>{a.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      )}
      <div className="min-w-[140px] flex-1">
        <label className="lux-label mb-1.5 block">Category</label>
        <Select value={value.gender || ANY} onValueChange={(v) => set("gender", v)}>
          <SelectTrigger className={trig} data-testid="results-filter-gender" aria-label="Category"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value={ANY}>Everyone</SelectItem>
            {Object.entries(GENDER_LABEL).map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      <div className="min-w-[150px] flex-1">
        <label className="lux-label mb-1.5 block">Ethnicity</label>
        <Select value={value.ethnicity || ANY} onValueChange={(v) => set("ethnicity", v)}>
          <SelectTrigger className={trig} data-testid="results-filter-ethnicity" aria-label="Ethnicity"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value={ANY}>Any ethnicity</SelectItem>
            {ETHNICITY_FILTERS.map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      <div className="min-w-[140px] flex-1">
        <label className="lux-label mb-1.5 block">Incall / outcall</label>
        <Select value={value.service || ANY} onValueChange={(v) => set("service", v)}>
          <SelectTrigger className={trig} data-testid="results-filter-service" aria-label="Incall or outcall"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value={ANY}>Either</SelectItem>
            {Object.entries(SERVICE_LABEL).map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      <div className="min-w-[180px] flex-1">
        <div className="mb-1.5 flex items-center justify-between">
          <span className="lux-label">Age</span>
          <span className="text-xs text-lux-silver" data-testid="results-filter-age-label">{ageMin} - {ageMax >= 60 ? "60+" : ageMax}</span>
        </div>
        <div className="flex h-11 items-center px-1">
          <Slider min={18} max={60} step={1} value={range} data-testid="results-filter-age"
            onValueChange={setRange}
            onValueCommit={([a, b]) => onChange({ ...value, age_min: a > 18 ? String(a) : undefined, age_max: b < 60 ? String(b) : undefined })}
            className="[&_[role=slider]]:border-lux-silver [&_[role=slider]]:bg-lux-silverBright" aria-label="Age range" />
        </div>
      </div>
      <div className="min-w-[180px] flex-1">
        <div className="mb-1.5 flex items-center justify-between">
          <span className="lux-label">Price range</span>
          <span className="text-xs text-lux-silver" data-testid="results-filter-price-label">£{priceMin} - {priceMax >= 1000 ? "£1000+" : `£${priceMax}`}</span>
        </div>
        <div className="flex h-11 items-center px-1">
          <Slider min={0} max={1000} step={10} value={priceRange} data-testid="results-filter-price"
            onValueChange={setPriceRange}
            onValueCommit={([a, b]) => onChange({ ...value, price_min: a > 0 ? String(a) : undefined, price_max: b < 1000 ? String(b) : undefined })}
            className="[&_[role=slider]]:border-lux-silver [&_[role=slider]]:bg-lux-silverBright" aria-label="Price range" />
        </div>
      </div>
      <div className="flex items-end gap-5 pb-2.5">
        <label className="flex cursor-pointer items-center gap-2 text-sm text-lux-text/90">
          <Switch checked={!!value.available} onCheckedChange={(c) => set("available", c ? "1" : undefined)} data-testid="results-filter-available" />
          Available today
        </label>
        <label className="flex cursor-pointer items-center gap-2 text-sm text-lux-text/90">
          <Switch checked={!!value.stories} onCheckedChange={(c) => set("stories", c ? "1" : undefined)} data-testid="results-filter-stories" />
          Has stories
        </label>
      </div>
    </>
  );
}

export default function Filters({ value, onApply, areas = [], showArea = true, total }) {
  const [draft, setDraft] = useState(value);
  const [open, setOpen] = useState(false);
  useEffect(() => setDraft(value), [value]);
  const active = useMemo(() => Object.keys(value).filter((k) => value[k]), [value]);
  const chipLabel = (k) => {
    if (k === "area") return areas.find((a) => a.slug === value.area)?.name || value.area;
    if (k === "gender") return GENDER_LABEL[value.gender];
    if (k === "ethnicity") return ETHNICITY_LABEL[value.ethnicity] || value.ethnicity;
    if (k === "service") return SERVICE_LABEL[value.service];
    if (k === "age_min") return `Age ${value.age_min}+`;
    if (k === "age_max") return `Up to ${value.age_max}`;
    if (k === "price_min") return `From £${value.price_min}`;
    if (k === "price_max") return `Up to £${value.price_max}`;
    if (k === "available") return "Available today";
    if (k === "stories") return "Has stories";
    return k;
  };
  const remove = (k) => {
    const n = { ...value };
    delete n[k];
    onApply(n);
  };
  return (
    <div data-testid="results-filters">
      <div className="hidden flex-wrap items-end gap-4 rounded-[12px] border border-lux-border bg-lux-surface p-4 md:flex" data-testid="results-filters-bar">
        <Controls value={value} onChange={onApply} areas={areas} showArea={showArea} />
      </div>
      <div className="flex items-center gap-2 md:hidden">
        <button onClick={() => setOpen(true)} className="lux-btn lux-btn-secondary lux-btn-sm" data-testid="results-filters-open-button">
          <SlidersHorizontal className="h-4 w-4" /> Filters{active.length ? ` (${active.length})` : ""}
        </button>
        {typeof total === "number" && <span className="text-xs text-lux-muted">{total} advertiser{total === 1 ? "" : "s"}</span>}
      </div>
      {active.length > 0 && (
        <div className="mt-3 flex flex-wrap items-center gap-2">
          {active.map((k) => (
            <button key={k} onClick={() => remove(k)} className="lux-chip hover:border-lux-silver/60" data-testid="results-filters-chip" aria-label={`Remove filter ${chipLabel(k)}`}>
              {chipLabel(k)} <X className="h-3 w-3 text-lux-silver" />
            </button>
          ))}
          <button onClick={() => onApply({})} className="text-xs text-lux-silver hover:text-lux-silverBright hover:underline" data-testid="results-filters-clear-button">Clear all</button>
        </div>
      )}
      <Drawer open={open} onOpenChange={setOpen}>
        <DrawerContent className="border-lux-border bg-lux-surface" data-testid="results-filters-drawer">
          <DrawerHeader className="text-left">
            <DrawerTitle className="font-serif text-2xl font-medium">Filters</DrawerTitle>
            <DrawerDescription className="text-lux-muted">Refine the advertisers you see.</DrawerDescription>
          </DrawerHeader>
          <div className="flex max-h-[60vh] flex-col gap-5 overflow-y-auto px-4">
            <Controls value={draft} onChange={setDraft} areas={areas} showArea={showArea} />
          </div>
          <DrawerFooter className="flex-row gap-2">
            <button onClick={() => setDraft({})} className="lux-btn lux-btn-ghost flex-1" data-testid="results-filters-drawer-clear">Clear</button>
            <button onClick={() => { onApply(draft); setOpen(false); }} className="lux-btn lux-btn-primary flex-[2]" data-testid="results-filters-apply-button">Show results</button>
          </DrawerFooter>
        </DrawerContent>
      </Drawer>
    </div>
  );
}
